"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Modal = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _classnames = _interopRequireDefault(require("classnames"));
var _react = _interopRequireWildcard(require("react"));
var _reactFocusLock = _interopRequireDefault(require("react-focus-lock"));
var _layers = require("../../../constants/layers");
var _getThemeClasses = require("../../../theme/utils/getThemeClasses");
var _useTheme = require("../../../theme/utils/useTheme");
var _StackOrder = require("../../../util/StackOrder");
var _LayerContext = require("../../../util/layerContext/LayerContext");
var _blanket = require("../../blanket");
var _portal = require("../../portal");
var _sharedVariables = require("../shared-variables");
var _Modal = require("../styled/Modal");
var _ModalAnimation = require("./ModalAnimation");
var _ModalTransition = require("./ModalTransition");
const warnOnDeprecatedFullscreenProps = (0, _lodestarUtils.warnOnce)("The `fullScreen` prop has been deprecated. Please use `placement='fullscreen'` instead.");
/**
 * The Modal component renders a modal/dialog on top of the page, above its content.
 * By itself, the Modal component handles most of the behavior of a modal, and is unopinionated
 * about its content--it's more or less a blank canvas.
 * That said, we have created some basic components that you can compose into Modal that aim
 * to handle most use-cases (see index.ts for this list).
 *
 * Modal is responsible for the following behaviors:
 * - Escape to close (if within a ModalStackIndexContext, multiple modals will be handled gracefully)
 * - Locking the browser scrolling so that the page behind the modal won't scroll
 * - Enter/Exit animations
 * - Modal positioning
 * - Portaling to escape parent overflow and scroll contexts
 * - Keeping browser tab focus within the modal
 */
const Modal = _ref => {
  let {
    fullScreen = false,
    shouldCloseOnEscapePress,
    shouldCloseOnOverlayClick = true,
    width = "medium",
    onCloseRequested,
    children,
    height,
    placement: providedPlacement,
    onCloseComplete,
    onOpenComplete,
    portalProps,
    clientId,
    components = {},
    isOpen,
    inFocusLock,
    gutterHeight,
    ...htmlAttributes
  } = _ref;
  // if not in production, warn if the deprecated props are used.
  if (process.env.NODE_ENV !== "production") {
    if (fullScreen) {
      warnOnDeprecatedFullscreenProps();
    }
  }
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const {
    style,
    className,
    ...otherHtmlAttributes
  } = htmlAttributes;
  const combinedClassName = (0, _classnames.default)((0, _getThemeClasses.getThemeClasses)(themeMode), className); //this enables custom classNames while still including required theme classNames
  const placement = getPlacementOrDefault(providedPlacement, fullScreen, themeMode);
  const {
    isActiveItem,
    isWithinStack
  } = (0, _StackOrder.useStackOrder)({
    isOpen,
    onCloseRequested,
    shouldCloseOnEscape: shouldCloseOnEscapePress !== undefined ?
    // If this is defined, use it.
    shouldCloseOnEscapePress :
    // Otherwise, fullScreen modals don't close on esc by default, but normal ones do.
    placement !== "fullscreen"
  });
  const PortalComponent = components && components.Portal || _portal.Portal;

  /**
   * Hook up the scroll handler
   */
  (0, _react.useEffect)(() => {
    if (isOpen) {
      // Prevent window from being scrolled programmatically so that the modal is positioned correctly
      // and to prevent scrollIntoView from scrolling the window.
      const handleWindowScroll = () => {
        window.scrollTo(window.pageXOffset, 0);
      };
      window.addEventListener("scroll", handleWindowScroll);
      return () => {
        window.removeEventListener("scroll", handleWindowScroll);
      };
    }
  }, [isOpen]);
  const handleOverlayClick = e => {
    e.stopPropagation();
    if (shouldCloseOnOverlayClick) {
      onCloseRequested();
    }
  };
  const handleOverlayDoubleClick = e => {
    e.stopPropagation();
  };
  const heightValue = placement === "fullscreen" ? "100vh" : height;
  // If width prop matches the width enum, allow styled component to consume as named prop.
  // Otherwise it's a custom width value (number, percentage, or string) and it will be consumed directly
  const widthName = width && _sharedVariables.WIDTH_ENUM.values.indexOf(width.toString()) !== -1 && placement !== "fullscreen" ? width : undefined;
  // redefine widthValue if a widthName is discovered
  const widthValue = placement === "fullscreen" ? "fullScreen" : widthName ? undefined : width;
  const modalWidth = (0, _Modal.modalWidth)({
    widthName,
    widthValue
  });
  const sharedBody = /*#__PURE__*/_react.default.createElement(_Modal.StyledModalContainer, (0, _extends2.default)({
    heightValue: heightValue,
    role: "dialog",
    onClick: e => e.stopPropagation(),
    onDoubleClick: e => e.stopPropagation(),
    themeMode: themeMode,
    "data-client-id": clientId,
    style: {
      ...style
    },
    className: combinedClassName
  }, otherHtmlAttributes), children);
  const modalBody = slide => placement === "fullscreen" ? sharedBody : /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_blanket.Blanket, {
    isTinted: true,
    onClick: handleOverlayClick,
    onDoubleClick: handleOverlayDoubleClick,
    clientId: clientId ? `modal-blanket-${clientId}` : "modal-blanket"
  }), /*#__PURE__*/_react.default.createElement(_Modal.PositionerAbsolute, {
    style: {
      ...slide,
      // Since this can be any value, apply it here so we don't create N different
      // stylesheets for each possible width.
      width: modalWidth
    },
    placement: placement,
    gutterHeight: gutterHeight,
    "data-client-id": "positioner-absolute"
  }, sharedBody));
  return /*#__PURE__*/_react.default.createElement(_LayerContext.LayerContextProvider, {
    defaultLayer: "modal"
  }, /*#__PURE__*/_react.default.createElement(_ModalTransition.ModalTransition, null, isOpen && /*#__PURE__*/_react.default.createElement(_ModalTransition.ModalTransitionConsumer, null, _ref2 => {
    let {
      isOpen,
      onExited
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(PortalComponent, (0, _extends2.default)({
      zIndex: _layers.layers.modal
    }, portalProps), /*#__PURE__*/_react.default.createElement(_ModalAnimation.ModalAnimation, {
      in: isOpen,
      onExited: e => {
        onCloseComplete?.(e);
        onExited?.();
      },
      onEntered: onOpenComplete
    }, _ref3 => {
      let {
        fade,
        slide
      } = _ref3;
      return /*#__PURE__*/_react.default.createElement(_Modal.FillScreen, {
        style: fade
      }, /*#__PURE__*/_react.default.createElement(_reactFocusLock.default, {
        whiteList: inFocusLock,
        disabled: isWithinStack && !isActiveItem || !isOpen
        // This rule doesn't apply here because we really DO
        // want to turn this behavior off :)
        // eslint-disable-next-line jsx-a11y/no-autofocus
        ,
        autoFocus: false
      }, modalBody(slide)));
    }));
  })));
};
exports.Modal = Modal;
function getPlacementOrDefault(placement, fullscreen, theme) {
  if (placement) {
    return placement;
  } else if (fullscreen) {
    return "fullscreen";
  }
  switch (theme) {
    // In Lodestar, the default is "top" so we maintain backwards-compatibility with anyone
    // who's designs expect it to be there.
    case "lodestar-light":
      return "top";
    // Going forward, all Ursa modals should be centered.
    default:
      return "center";
  }
}
//# sourceMappingURL=Modal.js.map